var _excluded = ["children", "type", "variant", "pristine", "invalid", "submitting", "className", "onClick"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import PropTypes from 'prop-types';
import { noop } from '../../utils';
import classnames from 'classnames';

/**
 *
 * A simple button component that can be used independently, or as part of a form.
 *
 * Conditionally adds classes and/or sets aria-disabled depending on passed props. If the button is `disabled` or `submitting`, the `onClick` handler will be overridden with a `noop`. This is especially helpful when preventing duplicate form submissions for **both** mouse and keyboard actions.
 *
 * In addition to the props below, any extra props will be passed directly to the inner `<button>` element.
 *
 * If a className is provided to the component, it will be appended to the conditionally added classes.
 *
 * _Note: Instead of targeting the `:disabled` pseudo-class or `[disabled]` attribute, you can target `[aria-disabled=true]` to apply similar styling. Using the ARIA attribute keeps the `<button>` in the taborder and will be read as "disabled" or "dimmed" by screen reader technologies. You can also target `.is-disabled` which gets added as a class based on the same conditions that set `aria-disabled`._
 *
 * @name Button
 * @type Function
 * @param {Boolean} [invalid] - Whether or not a related form is invalid (will set aria-disabled when `true`)
 * @param {Boolean} [pristine] - Whether or not a related form is pristine (will set aria-disabled when `true`)
 * @param {String} [variant="primary"] - A descriptive string that will be appended to the button's class with format `button-<type>`
 * @param {Boolean} [submitting] - Whether or not a related form is submitting (will give button class `'in-progress` when `true`)
 * @param {Boolean} [type="button"] - The [type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type) attribute of the button element
 * @param {Function} [children] - Any React component(s) being wrapped by the button
 * @example
 *
 * function MessageButton ({ message }) {
 *   return (
 *      <Button
 *        variant="secondary"
 *        onClick={ () => console.log(message) }
 *      >
 *        Print Message
 *      </Button>
 *   )
 * }
 *
 * // For a more in-depth example of using buttons with forms, see the docs for SubmitButton.
 *
 */

var propTypes = {
  invalid: PropTypes.bool,
  pristine: PropTypes.bool,
  variant: PropTypes.string,
  submitting: PropTypes.bool,
  type: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func
};
var defaultProps = {
  variant: 'primary',
  type: 'button',
  className: '',
  onClick: noop
};
function calculateClassName(_ref) {
  var className = _ref.className,
    variant = _ref.variant,
    pristine = _ref.pristine,
    invalid = _ref.invalid,
    submitting = _ref.submitting;
  return classnames("button-".concat(variant), {
    'is-disabled': pristine || invalid,
    'in-progress': submitting
  }, className);
}

// eslint-disable-next-line no-unused-vars
var Button = /*#__PURE__*/React.forwardRef(function Button(_ref2, ref) {
  var children = _ref2.children,
    type = _ref2.type,
    variant = _ref2.variant,
    pristine = _ref2.pristine,
    invalid = _ref2.invalid,
    submitting = _ref2.submitting,
    className = _ref2.className,
    onClick = _ref2.onClick,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var disabled = pristine || invalid;
  return /*#__PURE__*/React.createElement("button", _extends({
    type: type,
    ref: ref,
    className: calculateClassName({
      className: className,
      variant: variant,
      pristine: pristine,
      invalid: invalid,
      submitting: submitting
    }),
    "aria-disabled": pristine || invalid,
    onClick: disabled || submitting ? noop : onClick
  }, rest), children);
});
Button.propTypes = propTypes;
Button.defaultProps = defaultProps;
export default Button;